import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Zap, ShieldCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function AuthModal() {
  const { authOpen, setAuthOpen, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const close = () => {
    setAuthOpen(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res =
      mode === "login"
        ? await login(email, password)
        : await register(name, email, password);
    setLoading(false);
    if (res.ok) close();
    else setError(res.error);
  };

  return (
    <AnimatePresence>
      {authOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          data-testid="auth-modal-overlay"
        >
          <motion.div
            className="relative w-full max-w-md bg-aox-card border border-aox-cyan/30 clip-chamfer p-7 glow-cyan"
            initial={{ scale: 0.92, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.92, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={close}
              className="absolute right-4 top-4 text-aox-cyan/60 hover:text-aox-cyan transition-colors"
              data-testid="auth-modal-close"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="text-aox-green" size={22} />
              <span className="font-mono-aox text-xs uppercase tracking-[0.25em] text-aox-green">
                Hoodie Node Access
              </span>
            </div>
            <h2 className="font-display text-2xl font-bold text-white mb-6">
              {mode === "login" ? "Verify Identity" : "Become a Hoodie"}
            </h2>

            <button
              type="button"
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 bg-white text-[#111] font-semibold py-3 rounded-md clip-chamfer hover:-translate-y-0.5 transition-all mb-4"
              data-testid="auth-google-btn"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 mb-4">
              <span className="h-px flex-1 bg-aox-cyan/15" />
              <span className="font-mono-aox text-[10px] uppercase tracking-widest text-slate-500">
                or with email
              </span>
              <span className="h-px flex-1 bg-aox-cyan/15" />
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <input
                  className="w-full bg-aox-bg border border-aox-cyan/20 rounded-md px-4 py-3 text-sm text-white outline-none focus:border-aox-cyan focus:ring-2 focus:ring-aox-cyan/40 transition"
                  placeholder="Codename"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="auth-name-input"
                />
              )}
              <input
                type="email"
                className="w-full bg-aox-bg border border-aox-cyan/20 rounded-md px-4 py-3 text-sm text-white outline-none focus:border-aox-cyan focus:ring-2 focus:ring-aox-cyan/40 transition"
                placeholder="node@aox.io"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="auth-email-input"
              />
              <input
                type="password"
                className="w-full bg-aox-bg border border-aox-cyan/20 rounded-md px-4 py-3 text-sm text-white outline-none focus:border-aox-cyan focus:ring-2 focus:ring-aox-cyan/40 transition"
                placeholder="passphrase (min 6)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="auth-password-input"
              />

              {error && (
                <p className="text-sm text-red-400 font-mono-aox" data-testid="auth-error">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-aox-cyan text-aox-bg font-bold py-3 rounded-md clip-chamfer hover:-translate-y-0.5 hover:glow-cyan transition-all disabled:opacity-60"
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Zap size={18} />
                )}
                {mode === "login" ? "Enter The Grid" : "Activate Node"}
              </button>
            </form>

            <p className="text-center text-sm text-aox-cyan/60 mt-5">
              {mode === "login" ? "No node yet?" : "Already a hoodie?"}{" "}
              <button
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
                className="text-aox-green hover:underline font-semibold"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Become a Hoodie" : "Verify Identity"}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
